import React from "react";
import css from "./index.module.scss";
import { StrapiPicture } from "@/components/picture";
import ImageCard from "@/components/imageCard";
import GenericLayout from "layouts/generic";
import Button from "@/components/button";
import strapi from "scripts/strapi/index.js";
import Head from "next/head";
import CkEditorContent from "@/components/ckEditorContent";


export default function ArticlePage({article}) {
  const description = `${article.attributes.teaser}`;
  const title = `${article.attributes.headline} | Deviate Labs`;

  function trimTeaser(teaser){
    if (teaser.length > 100){
      return `${teaser.slice(0, 100).trim().replace(/ [A-z]*?$/, "")}...`;
    }
    return teaser;
  }

  return (
    <GenericLayout
      title={title}
      description={description}
      shareImageUrl={article?.attributes?.coverArt?.data?.attributes?.url}
      schema={ {
        "@context": "https://schema.org",
        "@type": "NewsArticle",
        "headline": article.attributes.headline,
        "image": [article?.attributes?.coverArt?.data?.attributes?.url],
        "datePublished": article?.attributes?.publishedAt,
        "dateModified": article?.attributes?.updatedAt,
        "author": [
          {
            "@type": "Person",
            "name": article?.attributes?.author,
            "url": "https://raymondfong.net/",
          },
        ],
      }}
    >
      <Head>
        <link rel="alternate" type="application/rss+xml" href="https://deviatelabs.com/api/rss"/>
      </Head>
      <section className={`section ${css.article}`}>
        <div className="sectionInner">
          <div className={css.row}>
            <div className={css.main}>
              <div className={css.introduction}>
                <h2>{article.attributes.headline}</h2>
                <div className={css.subheadline}>{article?.attributes?.teaser}</div>
                <div className={css.metadata}>
                  <span>{article.attributes.author}</span>
                  <span>{Math.round(article?.attributes?.body?.split(" ")?.length / 238)} min read</span>
                </div>
              </div>
              <div className={css.coverArt}>
                <StrapiPicture picture={article?.attributes?.coverArt?.data?.attributes} sizes="100vw" loading="eager"/>
              </div>
              <CkEditorContent content={article.attributes.body} className={css.article}/>

              <div className={css.cta}>
                <h2 className="underlined">Ready to Deviate?</h2>
                <Button label="Contact Us" href="/contact" ghost/>
              </div>
            </div>
            <aside>
              <div className="h3">More Articles</div>
              <div className={css.relatedArticles}>
                {(article?.attributes?.relatedArticles?.data || [])
                  .map((relatedArticle) => <ImageCard
                    key={relatedArticle.id}
                    headline={relatedArticle.attributes.headline}
                    href={`/resources/${relatedArticle.attributes.slug}`}
                    image={relatedArticle?.attributes?.coverArt?.data?.attributes}
                    imageSizes={"(max-width:1000px) 50vw, 500px"}
                    small
                  >
                    <div className={css.teaser}>{trimTeaser(relatedArticle.attributes.teaser)}</div>
                  </ImageCard>)}
              </div>
            </aside>
          </div>

        </div>
      </section>
    </GenericLayout>
  );
}

//one path for each article
export async function getStaticPaths() {
  let articles = (await strapi.article.findMany({
    fields: ["slug"],
  }));
  return {
    paths: articles.map((article) => {
      return {params: {article: article.attributes.slug}};
    }),
    fallback: "blocking",
  };
}

export async function getStaticProps(query) {
  let article = (await strapi.article.findMany({
    filters: {
      slug: {
        $eq: query.params.article,
      },
    },
    populate: {
      coverArt: "*",
      relatedArticles: {
        populate: {
          coverArt: "*",
        },
      },
    },
    fields: ["headline", "body", "author", "teaser", "publishedAt", "updatedAt"],
    publicationState: query.preview ? "preview" : "live",
  }))[0];

  if (!article){
    return {
      redirect: {
        destination: "/404",
        permanent: false,
      },
      revalidate: 60,
    };
  }

  //no hardcoded related articles so just take the most recent four
  if (article.attributes.relatedArticles.data.length === 0){
    article.attributes.relatedArticles.data = (await strapi.article.findMany({
      pagination: {
        page: 1,
        pageSize: 4,
      },
      filters: {
        id: {$ne: article.id}, //exclude the article the page is for
      },
      populate: {
        coverArt: "*",
      },
      fields: ["headline", "teaser", "slug"],
      publicationState: query.preview ? "preview" : "live",
    }));
  }

  return {
    props: {
      article,
    },
    revalidate: 60,
  };
}
